import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Typography } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';
import { parserNotices, setTotalsUnitQty, validateDraft } from '../../../lib/packingListValidation';
import { isSuccess, stableStringify } from '../../../lib/packingListRequest';
import { DOC_STATUS, fromDraftPayload, fromEditImpact, isEditDraft, markHeaderEdited, parseWarningsOf, toDraftPayload } from '../../../lib/packingListWire';
import { changedAllocationNotes, summarizeEditImpact } from '../../../lib/packingListEditImpact';
import { quantityIn } from '../../../lib/packingListQuantity';
import { confirmSucceeded } from '../../../store/packingListSlice';
import usePackingListFileUrl, { useOriginalDownload } from '../../../hooks/usePackingListFileUrl';
import SplitPane from './SplitPane';
import DocumentViewer from './DocumentViewer';
import HeaderFormPanel from './HeaderFormPanel';
import EditableGrid from './EditableGrid';
import ValidationBanner from './ValidationBanner';
import ConfirmBar from './ConfirmBar';
import EditModeBanner from './EditModeBanner';
import ImpactSummaryDialog from './ImpactSummaryDialog';
import DeleteDocumentDialog from '../items/DeleteDocumentDialog';

const TOTAL_TAB_FIELDS = ['total_qty', 'total_carton_qty', 'total_gross_weight', 'total_net_weight', 'total_volume'];
/** 합계 대조 탭에서 고치는 칸인지 — 헤더 합계 칸이거나 합계행 전용 단위(totals.<단위>). */
const isTotalsField = (field) => TOTAL_TAB_FIELDS.includes(field) || String(field ?? '').startsWith('totals.');
/** 수정을 연 뒤 동료가 문서(VERSION_CONFLICT)나 배분(ALLOCATION_VERSION_CONFLICT)을 바꿨다는 거절. 둘 다 이 수정을 버리고 닫는다. */
const EDIT_CONFLICT_CODES = ['VERSION_CONFLICT', 'ALLOCATION_VERSION_CONFLICT'];

/**
 * 초안 문서를 검수해 확정하는 검수 화면. 상단 검증 띠, 좌 원본 뷰어, 우측은 헤더 패널(상)과 라인 그리드(하),
 * 하단 확정 바로 구성된다. doc은 서버에서 읽은 문서이고 화면은 그 편집본(draft_payload)을 고친다.
 * 임시저장은 Save Draft를 누를 때만 하고, 저장되면 창을 닫는다. 저장하지 않은 내용이 있으면 떠나기 전에 다이얼로그가 한 번 더 묻는다(leaveGuardRef).
 * 수기로 새로 시작한 문서(id 없음)는 서버에 아직 없으며, 처음 Save Draft·Confirm할 때 문서를 만든 뒤 저장한다.
 * 저장·확정·폐기는 서버가 성공을 돌려준 뒤에만 창을 닫고, 실패하면 편집 내용을 그대로 둔다.
 * 배분은 여기서 하지 않는다.
 * 파싱이 실패한 문서는 빈 그리드로 열리며 상단에 실패 안내와 Retry parsing을 둔다.
 * 확정본 수정(isEditDraft)이면 수정 모드다. 서버에는 수정 초안이 없고 고친 내용은 이 화면에만 있다. 닫으면 아무것도 저장하지 않는다. Delete는 확정 문서를 아이템·배분째 지운다.
 * 배분된 라인 표시와 라인별 배분 영향 안내는 화면에서 바로 계산한 힌트다. Confirm을 누르면 서버에 영향 요약을 물어 보여주고,
 * 동의하면 재확정(confirm_edit)을 보낸다. 요약 수치와 재확정 후 미배분 건수는 서버 값이다.
 * 그 사이 동료가 문서나 배분을 바꿨으면(409) 이 수정은 버리고 창을 닫는다 — 먼저 저장된 쪽이 남는다.
 * confirmedItems는 이 문서의 확정 품목, allocations는 품목 id → 배분 몫이다(수정 모드에서만 쓴다).
 * readOnly면 확정본 보기다 — 모든 칸을 읽기만 하고, 하단 오른쪽은 Download(원본이 있을 때)와 Edit뿐이다. Edit을 누르면 onStartEdit으로 수정 모드가 된다.
 * 수정 모드의 Read-only는 onBackToView로 다이얼로그에 맡긴다 — 고친 내용이 있으면 다이얼로그가 버릴지 묻고, 버리면 확정본 보기로 다시 연다.
 */
export default function PLPreview({ hbl, doc, workspaceKey, confirmedDocs = [], confirmedItems = [], allocations = {}, allocationVersion, commands, onConfirmed, onDraftSaved, onDeleted, onEditCancelled, onVersionChanged, leaveGuardRef, readOnly = false, onStartEdit, onBackToView, onRetryParsing = null }) {
    const dispatch = useDispatch();
    const apiRef = useGridApiRef();
    const { uomMaster, uomAliases, partyAliases } = useSelector((s) => s.packingList);
    /** 편집 상태는 문서가 열리는 순간 한 번만 채운다. 작업 영역을 다시 읽어도 입력을 덮어쓰지 않는다(이 컴포넌트는 문서 id로 key가 걸려 있다). */
    const [initial] = useState(() => fromDraftPayload(doc.payload));
    const [header, setHeader] = useState(initial.header);
    const [lines, setLines] = useState(initial.lines);
    /** PL 하단에 적힌 컨테이너 번호. 파싱이 잘못 읽었으면 담당자가 고치거나 지운다. */
    const [containerNumbers, setContainerNumbers] = useState(initial.containerNumbers);
    /** 문서 합계행(파서가 읽은 totals). 팔레트처럼 헤더 칸이 없는 단위는 여기서만 고친다. 수기 문서는 null. */
    const [totals, setTotals] = useState(initial.totals);
    const changeTotalsUnit = (unit, qty) => setTotals((t) => setTotalsUnitQty(t, unit, qty));
    /** 서버 문서 id. 수기 새 문서는 처음 저장할 때 생긴다. */
    const [docId, setDocId] = useState(doc.id);
    /** 저장할 때마다 서버가 올려 주는 문서 version. 다음 요청은 이 값으로 보낸다. */
    const [version, setVersion] = useState(doc.version);
    const [activeRef, setActiveRef] = useState(null);
    const [highlight, setHighlight] = useState({ field: null, cell: null });
    const [headerTab, setHeaderTab] = useState('info');
    const [headerExpanded, setHeaderExpanded] = useState(true);
    const [saving, setSaving] = useState(false);
    /**
     * 서버 요청(저장·확정·삭제)이 진행 중인지. 렌더를 기다리지 않고 바로 보이도록 ref로 둔다.
     * 진행 중에는 Save Draft·Confirm을 또 보내지 않는다 — 수기 새 문서가 두 번 만들어지거나 같은 version으로 두 번 저장되는 것을 막는다.
     * X·Esc·Other document로도 떠나지 않는다 — 그 요청이 끝나며 창을 닫거나 결과를 보여준다.
     */
    const pendingRef = useRef(false);
    /** 확정을 시도하며 초안이 서버에 저장됐는지. 확정이 거절돼도 그 내용은 Draft로 남으므로 떠날 때 업로드를 지우지 않는다. */
    const persistedRef = useRef(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    /** 다시 파싱 확인 창. 입력한 내용이 파서 결과로 덮이므로 먼저 묻는다. */
    const [reparseOpen, setReparseOpen] = useState(false);
    const parseWarnings = useMemo(() => parseWarningsOf(doc), [doc]);
    /**
     * 원본이 있는 문서인지. 수기 문서(새로 시작했거나 수기로 확정한 문서)는 원본이 없어 왼쪽 문서 미리보기 영역을 두지 않는다.
     * 수기 새 문서가 처음 저장돼 id가 생겨도 원본은 없으므로 input_source로 가른다. 수정(edit) 작업은 자기 파일이 없으므로 확정 원본 유무로 본다.
     */
    const hasOriginal = docId != null && doc.input_source !== 'manual' && !(doc.input_source === 'edit' && doc.il_file_id_current == null);
    /** 원본 뷰어 URL. 수정(edit) 작업은 파일이 없으므로 서버가 현재 확정 원본을 준다. */
    const file = usePackingListFileUrl(commands, { docId, hasFile: hasOriginal });
    const { download, downloading } = useOriginalDownload(commands);
    const editing = isEditDraft(doc);
    /** 재확정 전에 서버가 준 영향 요약(fromEditImpact). null이면 다이얼로그가 닫혀 있다. */
    const [serverImpact, setServerImpact] = useState(null);

    /** 요청 하나를 진행 중으로 표시해 실행한다. 저장 중 입력은 편집 영역을 inert로 막고, 지금 포커스된 칸에서도 빼 둔다. */
    const runPending = async (task) => {
        pendingRef.current = true;
        setSaving(true);
        document.activeElement?.blur?.();
        try {
            return await task();
        } finally {
            pendingRef.current = false;
            setSaving(false);
        }
    };

    /** House 당사자는 HBL 행이 가진 Trade Partner 값을 쓴다. 이름이 없으면(미지정) 당사자 대조를 건너뛴다. */
    const houseShipper = useMemo(() => (hbl.shipper_name ? { id: hbl.shipper_id, name: hbl.shipper_name } : null), [hbl.shipper_id, hbl.shipper_name]);
    const houseConsignee = useMemo(() => (hbl.consignee_name ? { id: hbl.consignee_id, name: hbl.consignee_name } : null), [hbl.consignee_id, hbl.consignee_name]);

    const blContainerNumbers = useMemo(() => (hbl.containers ?? []).map((c) => c.cntr_number).filter(Boolean), [hbl.containers]);

    const otherConfirmed = useMemo(() => confirmedDocs.filter((d) => d.id !== docId), [confirmedDocs, docId]);

    const validation = useMemo(
        () => validateDraft({ header, lines, totals, hbl, houseShipper, houseConsignee, partyAliases, uomMaster, uomAliases, docContainerNumbers: containerNumbers.filter((n) => n.trim()), confirmedInvoiceNos: otherConfirmed.map((d) => d.invoice_no), parseWarnings }),
        [header, lines, totals, containerNumbers, hbl, houseShipper, houseConsignee, partyAliases, uomMaster, uomAliases, otherConfirmed, parseWarnings],
    );

    /** 파서만 아는 칸 단위 알림(후보가 둘이던 칸). 원본(parsed_payload)에서 읽어 편집으로 낡지 않는다. 문서 단위 판정은 검증 경고로 올라간다. */
    const notices = useMemo(() => parserNotices(doc.parsed_payload), [doc.parsed_payload]);

    /** 헤더 칸을 고치면 손댄 칸으로 기록해 그 칸의 신뢰도 점·경고를 거둔다. */
    const changeHeader = (field, value) => setHeader((h) => markHeaderEdited({ ...h, [field]: value }, field));

    /** 실패한 문서를 다시 파싱한다. 입력한 내용이 있으면 덮인다고 먼저 묻는다. */
    const parseFailed = doc.parse_result === 'failed' && doc.input_source !== 'manual' && !editing;
    const requestReparse = () => {
        if (dirty || lines.length > 0) setReparseOpen(true);
        else runReparse();
    };
    const runReparse = async () => {
        setReparseOpen(false);
        if (pendingRef.current || !onRetryParsing || docId == null) return;
        await runPending(() => onRetryParsing({ id: docId, version, parseGeneration: doc.parse_generation, filename: file.file?.original_filename ?? null }));
    };

    /** 수정 모드의 라인별 배분 영향 힌트(마커·배너). 재확정 결과는 서버가 정하므로 요약 수치에는 쓰지 않는다. */
    const impact = useMemo(() => (editing ? summarizeEditImpact({ lines, items: confirmedItems, allocations, containers: hbl.containers }) : null), [editing, lines, confirmedItems, allocations, hbl.containers]);
    const allocationNotes = useMemo(() => (impact ? changedAllocationNotes(lines, impact.byLine) : []), [impact, lines]);

    const issuesByCell = useMemo(() => {
        const map = {};
        validation.warnings.filter((w) => w.scope === 'line').forEach((w) => (map[`${w.rowId}:${w.field}`] = 'pl-cell-warning'));
        validation.errors.filter((e) => e.scope === 'line').forEach((e) => (map[`${e.rowId}:${e.field}`] = 'pl-cell-error'));
        return map;
    }, [validation]);

    const focusLine = (lineId, field = 'description') => {
        const line = lines.find((l) => l.id === lineId);
        setActiveRef(line?.source_ref ? { ...line.source_ref, lineId } : { lineId });
        const rowIndex = lines.indexOf(line);
        if (rowIndex >= 0) apiRef.current?.scrollToIndexes?.({ rowIndex });
        apiRef.current?.setCellFocus?.(lineId, field);
    };

    /** 검증 항목을 누르면 해당 셀(라인) 또는 헤더 필드(탭 전환 포함)로 데려가고 잠깐 강조한다. */
    const jumpTo = (issue) => {
        if (issue.scope === 'line') {
            focusLine(issue.rowId, issue.field);
            setHighlight({ field: null, cell: { rowId: issue.rowId, field: issue.field } });
        } else {
            setHeaderTab(isTotalsField(issue.field) ? 'totals' : 'info');
            setHeaderExpanded(true);
            setHighlight({ field: issue.field, cell: null });
            setTimeout(() => document.getElementById(`header-${issue.field}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' }), 50);
        }
        setTimeout(() => setHighlight({ field: null, cell: null }), 3000);
    };

    /**
     * 서버에 아직 저장되지 않은 내용이 있는지. 마지막으로 저장한(처음엔 연 순간의) 내용과 서버에 보낼 모양으로 비교해,
     * 화면 전용 값(행 선택 등)의 변화는 변경으로 보지 않는다. 수기 새 문서는 빈 상태에서 열리므로 이 값이 곧 "내용이 있는지"다.
     */
    const [saved, setSaved] = useState(initial);
    // 화면이 고치는 값만 비교한다. 원본(raw_document)처럼 화면이 고치지 않는 큰 값까지 매 입력마다 직렬화하지 않는다.
    const savedPayloadKey = useMemo(() => stableStringify(toDraftPayload({ header: saved.header, lines: saved.lines, containerNumbers: saved.containerNumbers, totals: saved.totals })), [saved]);
    const dirty = useMemo(() => stableStringify(toDraftPayload({ header, lines, containerNumbers, totals })) !== savedPayloadKey, [header, lines, containerNumbers, totals, savedPayloadKey]);

    /** Discard changes 직전의 편집 상태. 잘못 눌렀을 때 알림의 Undo로 되살린다. null이면 알림 없음. */
    const [discarded, setDiscarded] = useState(null);

    /** 이번에 연 뒤로 고친 내용을 버리고 마지막으로 저장된 상태(처음엔 연 순간)로 되돌린다. 서버 요청은 없다. */
    const discardChanges = () => {
        setDiscarded({ header, lines, containerNumbers, totals });
        setHeader(saved.header);
        setLines(saved.lines);
        setContainerNumbers(saved.containerNumbers);
        setTotals(saved.totals);
        setActiveRef(null);
    };
    /** 되돌린 뒤 다시 고치면 Undo를 거둔다. 그대로 두면 Undo가 새로 고친 내용을 덮어 버린다. */
    useEffect(() => {
        if (discarded && (header !== saved.header || lines !== saved.lines || containerNumbers !== saved.containerNumbers || totals !== saved.totals)) setDiscarded(null);
    }, [header, lines, containerNumbers, totals, saved, discarded]);
    const undoDiscard = () => {
        if (!discarded) return;
        setHeader(discarded.header);
        setLines(discarded.lines);
        setContainerNumbers(discarded.containerNumbers);
        setTotals(discarded.totals);
        setDiscarded(null);
    };

    /**
     * 저장할 서버 문서. 수기 새 문서면 이때 만든다. 만든 뒤 저장이 실패해도 id·version을 남겨 다시 시도할 때 문서를 또 만들지 않는다.
     * 만들어진 문서가 카드 목록에 보이도록 작업 영역도 다시 읽는다.
     */
    const ensureDocument = async () => {
        if (docId != null) return { id: docId, version };
        const created = await commands.createManualDocument();
        const id = created.data?.document_id;
        if (!isSuccess(created) || id == null) return null;
        const createdVersion = created.data.version ?? null;
        setDocId(id);
        setVersion(createdVersion);
        onVersionChanged?.();
        return { id, version: createdVersion };
    };

    /**
     * Save Draft. 초안을 서버에 저장하는 유일한 길이고, 저장되면 창을 닫는다. 고친 내용이 없으면 보내지 않는다 —
     * 열어 보기만 해도 version이 올라 동료의 저장을 409로 막지 않게.
     * 동료가 먼저 저장해 version이 어긋나면(409) 저장하지 않고 알린다. 다시 열어 최신본을 봐야 한다.
     * 그 밖의 실패는 요청이 띄운 오류 스낵바로 알리고 입력은 그대로 둔 채 검수 화면에 남는다.
     */
    const saveDraft = async () => {
        if (pendingRef.current || !dirty) return;
        const snapshot = { header, lines, containerNumbers, totals };
        const saved = await runPending(async () => {
            const target = await ensureDocument();
            if (!target) return false;
            const result = await commands.saveDraft({ id: target.id, version: target.version, draftPayload: toDraftPayload({ ...snapshot, base: doc.payload }), quietCodes: ['VERSION_CONFLICT'] });
            if (result.code === 'VERSION_CONFLICT') {
                dispatch(setSnackbar({ open: true, severity: 'warning', message: 'Your changes were not saved because someone else changed this draft. Close it and open it again to see the latest version.' }));
                return false;
            }
            return isSuccess(result);
        });
        if (!saved) return;
        dispatch(setSnackbar({ open: true, severity: 'success', message: 'Draft saved.' }));
        onDraftSaved?.();
    };

    /** 동료가 먼저 문서나 배분을 바꿔 재확정이 거절됐다. 이 수정은 버리고 닫는다 — 다시 열면 최신 확정본에서 시작한다. */
    const discardStaleEdit = () => {
        dispatch(setSnackbar({ open: true, severity: 'warning', message: 'Someone else changed this packing list or its allocation first. Your edit was discarded. Open it again to edit the latest version.' }));
        onEditCancelled?.();
    };

    const editRequest = () => ({ id: docId, version, allocationVersion: doc.baseAllocationVersion, draftPayload: toDraftPayload({ header, lines, containerNumbers, totals, base: doc.payload }), quietCodes: EDIT_CONFLICT_CODES });

    /** 재확정 전 서버 영향 요약. 서버는 저장하지 않는다. */
    const requestEditImpact = async () => {
        const result = await runPending(() => commands.readEditImpact(editRequest()));
        if (EDIT_CONFLICT_CODES.includes(result.code)) return discardStaleEdit();
        if (isSuccess(result)) setServerImpact(fromEditImpact(result.data));
    };

    /** 영향 요약에 동의한 재확정. 서버가 다시 검증하고 거절하면 저장하지 않고 수정 화면에 남는다. */
    const submitEditConfirm = async () => {
        if (pendingRef.current) return;
        const result = await runPending(() => commands.confirmEdit(editRequest()));
        setServerImpact(null);
        if (EDIT_CONFLICT_CODES.includes(result.code)) return discardStaleEdit();
        if (!isSuccess(result)) return;
        dispatch(confirmSucceeded({ key: workspaceKey, documentId: docId, editImpact: { unallocated: Number(result.data?.unallocated ?? 0) } }));
        dispatch(setSnackbar({ open: true, severity: 'success', message: `PL ${header.invoice_no ?? ''} re-confirmed (${lines.length} lines)` }));
        onConfirmed?.();
    };
    /**
     * 떠나려 할 때 다이얼로그가 읽는 지금 상태. 요청이 진행 중이면 떠나지 않고, 저장하지 않은 내용이 있으면 한 번 더 묻는다.
     * 수정 모드는 서버에 보낼 것이 없다 — 떠나면 고친 내용만 버려지고 확정본은 그대로다.
     */
    if (leaveGuardRef) leaveGuardRef.current = () => ({ pending: pendingRef.current, dirty, version, persisted: persistedRef.current });

    const handleDelete = async () => {
        // 서버에 아직 없는 수기 새 문서는 지울 것이 없으니 화면만 닫는다.
        if (docId == null) {
            setDeleteOpen(false);
            onDeleted?.();
            return;
        }
        if (pendingRef.current) return;
        const result = await runPending(() => commands.deleteDocument({ id: docId, version, allocationVersion }));
        if (!isSuccess(result)) return;
        setDeleteOpen(false);
        onDeleted?.();
    };

    /** 수정 모드는 서버 영향 요약을 먼저 띄우고, 거기서 동의하면 재확정한다. */
    const handleConfirm = () => {
        if (pendingRef.current) return;
        if (editing) requestEditImpact();
        else submitConfirm();
    };

    const submitConfirm = async () => {
        if (pendingRef.current) return;
        const snapshot = { header, lines, containerNumbers, totals };
        const outcome = await runPending(async () => {
            const target = await ensureDocument();
            if (!target) return null;
            return { target, result: await commands.confirm({ id: target.id, version: target.version, allocationVersion, draftPayload: toDraftPayload({ ...snapshot, base: doc.payload }) }) };
        });
        if (!outcome) return;
        const { target, result } = outcome;
        if (result.savedVersion != null) {
            // 확정 전 임시저장은 끝났다. 서버 초안이 이 내용이므로 "마지막 저장 상태"도 맞춘다 — 안 그러면 Discard changes가
            // 연 순간으로 되돌린 뒤 저장할 것이 없다고 보고 닫아, 버렸다고 믿은 편집이 서버에 남는다.
            setVersion(result.savedVersion);
            setSaved(snapshot);
            persistedRef.current = true;
        }
        if (!isSuccess(result)) {
            // 임시저장은 끝나 version이 올랐으므로 문서 카드도 서버 값으로 맞춘다. 안 그러면 카드에서 삭제할 때 옛 version이 나가 409가 난다.
            if (result.savedVersion != null) onVersionChanged?.();
            return;
        }
        dispatch(confirmSucceeded({ key: workspaceKey, documentId: target.id, editImpact: null }));
        dispatch(setSnackbar({ open: true, severity: 'success', message: `PL ${header.invoice_no ?? ''} confirmed (${lines.length} lines)` }));
        onConfirmed?.();
    };

    /** 확정 바에 보여 주는 요약 수치. 합계 대조와는 별개로 라인을 그냥 더한 값이다. */
    const summary = {
        qty: lines.reduce((s, l) => s + (Number(l.qty) || 0), 0),
        cartons: lines.reduce((s, l) => s + (quantityIn(l, 'CTN') ?? 0), 0),
        weight: Number(header.total_gross_weight) || lines.reduce((s, l) => s + (Number(l.gross_weight) || 0), 0),
    };

    /** 오른쪽 헤더·라인 편집 영역. 원본이 없는 수기 문서는 이 영역만 전체 너비로 쓴다. */
    const form = (
        <Box sx={{ height: '100%', p: 1, bgcolor: '#f3f4f3' }}>
            <SplitPane
                direction="vertical"
                firstCollapsed={!headerExpanded}
                first={<HeaderFormPanel header={header} onChange={changeHeader} totals={totals} onTotalsChange={changeTotalsUnit} containerNumbers={containerNumbers} onContainerNumbersChange={setContainerNumbers} blContainerNumbers={blContainerNumbers} lines={lines} houseShipper={houseShipper} houseConsignee={houseConsignee} partyAliases={partyAliases} onRegisterAlias={(tradePartnerId, alias) => commands.addPartyAlias({ tradePartnerId, alias })} onBatchApplyLot={(lot, expiry) => setLines((ls) => ls.map((l) => ({ ...l, lot_no: lot, expiry_date: expiry ?? l.expiry_date })))} highlightField={highlight.field} activeTab={headerTab} onTabChange={setHeaderTab} expanded={headerExpanded} onExpandedChange={setHeaderExpanded} readOnly={readOnly} />}
                second={<Box sx={{ height: '100%', pt: 1 }}><EditableGrid apiRef={apiRef} lines={lines} onLinesChange={setLines} activeRef={activeRef} onActiveRef={setActiveRef} highlightCell={highlight.cell} issuesByCell={issuesByCell} uomAliases={uomAliases} onUomAlias={(raw, code) => commands.addUomAlias({ raw, code })} lineMarkers={impact?.byLine ?? null} readOnly={readOnly} /></Box>}
            />
        </Box>
    );

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, bgcolor: 'white' }}>
            {parseFailed && (
                <Alert
                    severity="warning"
                    sx={{ py: 0, fontSize: 12, borderRadius: 0, alignItems: 'center' }}
                    action={onRetryParsing && docId != null ? <Button size="small" color="inherit" onClick={requestReparse} disabled={saving} sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>Retry parsing</Button> : undefined}
                >
                    <b>Parsing failed</b> for {file.file?.original_filename ?? 'this file'}. The grid is empty — enter the lines manually while viewing the original, or retry parsing.
                </Alert>
            )}
            {editing && !readOnly && <EditModeBanner notes={allocationNotes} onJump={(lineId) => focusLine(lineId, 'qty')} />}
            {!readOnly && notices.map((n) => <Alert key={n.code} severity="info" sx={{ py: 0, fontSize: 12, borderRadius: 0 }}>{n.message}</Alert>)}
            {!readOnly && <ValidationBanner validation={validation} onJump={jumpTo} />}
            {/* 저장 중에는 편집을 막는다. 응답을 기다리는 동안 고친 내용은 보낸 요청에 없어서, 저장 후 창이 닫히면 사라진다. */}
            <Box sx={{ flex: 1, minHeight: 0, opacity: saving ? 0.6 : 1 }} inert={saving ? '' : undefined}>
                {hasOriginal ? (
                    <SplitPane
                        first={<DocumentViewer doc={doc} raw={doc.payload?.raw_document} lines={lines} activeRef={activeRef} onPickLine={(id) => focusLine(id)} file={file} onDownload={() => download(docId)} downloading={downloading} />}
                        second={form}
                    />
                ) : (
                    form
                )}
            </Box>
            <ConfirmBar
                errorCount={validation.errorCount}
                itemCount={lines.length}
                totalQty={summary.qty}
                totalCartons={summary.cartons}
                totalWeight={summary.weight}
                weightUom={header.weight_uom}
                saving={saving}
                dirty={dirty}
                onDelete={() => setDeleteOpen(true)}
                canDiscardChanges={dirty}
                onDiscardChanges={discardChanges}
                canSaveDraft={dirty}
                onSaveDraft={saveDraft}
                onConfirm={handleConfirm}
                editing={editing}
                readOnly={readOnly}
                onEdit={onStartEdit}
                onReadOnly={editing ? onBackToView : undefined}
                onDownload={hasOriginal ? () => download(docId) : undefined}
                downloading={downloading}
            />
            <ImpactSummaryDialog open={Boolean(serverImpact)} impact={serverImpact} invoiceNo={header.invoice_no} saving={saving} onCancel={() => setServerImpact(null)} onConfirm={submitEditConfirm} />
            <Snackbar
                open={Boolean(discarded)}
                autoHideDuration={6000}
                onClose={(_, reason) => reason !== 'clickaway' && setDiscarded(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                message="Changes discarded."
                action={<Button size="small" color="secondary" onClick={undoDiscard} sx={{ fontSize: 12 }}>Undo</Button>}
            />
            <Dialog open={reparseOpen} onClose={() => !saving && setReparseOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: 14, color: '#003a52' }}>Retry parsing?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 12 }}>The lines and header will be replaced with the parser result when it finishes. Anything you typed into this draft is lost.</Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button size="small" onClick={() => setReparseOpen(false)} disabled={saving} sx={{ fontSize: 12 }}>Cancel</Button>
                    <Button size="small" variant="contained" onClick={runReparse} disabled={saving} sx={{ fontSize: 12 }}>Retry parsing</Button>
                </DialogActions>
            </Dialog>
            <DeleteDocumentDialog doc={deleteOpen ? { invoice_no: saved.header.invoice_no, status: editing ? DOC_STATUS.CONFIRMED : DOC_STATUS.DRAFT } : null} busy={saving} onCancel={() => setDeleteOpen(false)} onConfirm={handleDelete} />
        </Box>
    );
}
